import React, { useState } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { useUserStore } from '../../stores/userStore';
import { ShopItem, ItemRarity } from '../../shared/types';
import { RARITY_COLORS } from '../../shared/constants';
import { formatNumber } from '../../shared/utils';

const SHOP_ITEMS: ShopItem[] = [
  { id: 'skin_cyber', name: 'Cyber Strike', description: 'Neon-infused combat skin', type: 'skin', rarity: 'epic', price: 1500, image: '', featured: true, limited: false, tags: ['skin', 'epic'] },
  { id: 'skin_neon', name: 'Neon Ghost', description: 'Phantom of the arena', type: 'skin', rarity: 'legendary', price: 3000, originalPrice: 4000, discount: 25, image: '', featured: true, limited: true, expiresAt: '2026-08-01', tags: ['skin', 'legendary'] },
  { id: 'emote_victory', name: 'Victory Dance', description: 'Celebrate your wins', type: 'emote', rarity: 'rare', price: 500, image: '', featured: false, limited: false, tags: ['emote'] },
  { id: 'bundle_starter', name: 'Starter Bundle', description: 'Everything to get started', type: 'bundle', rarity: 'uncommon', price: 800, originalPrice: 1200, discount: 33, image: '', featured: true, limited: false, tags: ['bundle', 'starter'] },
  { id: 'crate_legendary', name: 'Legendary Crate', description: 'Contains a legendary item', type: 'crate', rarity: 'mythic', price: 2500, image: '', featured: true, limited: false, tags: ['crate'] },
  { id: 'title_ace', name: 'Ace Pilot', description: 'Premium title', type: 'title', rarity: 'rare', price: 300, image: '', featured: false, limited: false, tags: ['title'] },
  { id: 'icon_flame', name: 'Flame Icon', description: 'Fiery profile icon', type: 'icon', rarity: 'uncommon', price: 200, image: '', featured: false, limited: false, tags: ['icon'] },
  { id: 'banner_storm', name: 'Storm Banner', description: 'Electric banner', type: 'banner', rarity: 'rare', price: 400, image: '', featured: false, limited: false, tags: ['banner'] },
];

export function ShopScreen() {
  const setScreen = useGameStore(s => s.setScreen);
  const wallet = useUserStore(s => s.wallet);
  const spendCurrency = useUserStore(s => s.spendCurrency);
  const addItem = useUserStore(s => s.addItem);
  const [activeTab, setActiveTab] = useState('featured');
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);

  const tabs = [
    { id: 'featured', label: 'Featured' },
    { id: 'skins', label: 'Skins' },
    { id: 'crates', label: 'Crates' },
    { id: 'emotes', label: 'Emotes' },
    { id: 'bundles', label: 'Bundles' },
    { id: 'daily', label: 'Daily Rotation' },
  ];

  const filteredItems = SHOP_ITEMS.filter((item) => {
    if (activeTab === 'featured') return item.featured;
    if (activeTab === 'daily') return true;
    return item.type === activeTab.slice(0, -1) || (activeTab === 'skins' && item.type === 'skin');
  });

  const handlePurchase = (item: ShopItem) => {
    if (spendCurrency(item.price, `Purchased ${item.name}`)) {
      addItem({
        id: `inv_${Date.now()}`,
        type: item.type === 'bundle' ? 'skin' : item.type as 'skin' | 'title' | 'banner' | 'emote' | 'icon' | 'crate' | 'key',
        itemId: item.id,
        quantity: 1,
        acquiredAt: new Date().toISOString(),
        tradeable: item.rarity !== 'mythic',
      });
      setSelectedItem(null);
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-cf-black">
      <div className="flex items-center justify-between px-6 py-4 border-b border-cf-border">
        <h2 className="text-xl font-bold text-cf-white">Shop</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-cf-panel/60 border border-cf-border rounded-lg">
            <div className="w-3 h-3 rounded-full bg-cf-amber" />
            <span className="text-cf-amber font-bold text-sm">{formatNumber(wallet.balance)}</span>
            <span className="text-cf-muted text-xs">TR</span>
          </div>
          <button onClick={() => setScreen('menu')} className="cf-btn-secondary text-xs">
            Back
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-48 border-r border-cf-border p-3 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-cf-cyan/10 text-cf-cyan border border-cf-cyan/20'
                  : 'text-cf-muted hover:text-cf-text hover:bg-cf-dark'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="cf-panel p-4 text-left hover:border-cf-cyan/30 transition-all group"
              >
                <div className="aspect-square rounded-lg bg-cf-dark mb-3 flex items-center justify-center overflow-hidden">
                  <div
                    className="w-full h-full bg-gradient-to-br opacity-30"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${RARITY_COLORS[item.rarity]}, transparent)`,
                    }}
                  />
                  <div className="absolute text-3xl font-black opacity-20">
                    {item.type === 'skin' ? '🎨' : item.type === 'crate' ? '📦' : item.type === 'emote' ? '💃' : '🏷'}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: RARITY_COLORS[item.rarity] }} />
                  <span className="text-[10px] uppercase font-bold" style={{ color: RARITY_COLORS[item.rarity] }}>
                    {item.rarity}
                  </span>
                  {item.limited && (
                    <span className="text-[10px] text-cf-red font-bold">LIMITED</span>
                  )}
                </div>
                <h4 className="text-sm font-bold text-cf-white truncate">{item.name}</h4>
                <p className="text-xs text-cf-muted mt-0.5 truncate">{item.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm font-bold text-cf-amber">{formatNumber(item.price)} TR</span>
                  {item.originalPrice && (
                    <span className="text-xs text-cf-muted line-through">{formatNumber(item.originalPrice)}</span>
                  )}
                  {item.discount && (
                    <span className="text-[10px] text-cf-green font-bold">-{item.discount}%</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedItem && (
        <div className="absolute inset-0 bg-cf-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="cf-panel p-6 w-full max-w-md animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-cf-white">{selectedItem.name}</h3>
              <button onClick={() => setSelectedItem(null)} className="text-cf-muted hover:text-cf-white text-xl">✕</button>
            </div>
            <div className="aspect-video rounded-lg bg-cf-dark mb-4 flex items-center justify-center">
              <div className="text-5xl opacity-30">
                {selectedItem.type === 'skin' ? '🎨' : selectedItem.type === 'crate' ? '📦' : '🏷'}
              </div>
            </div>
            <p className="text-sm text-cf-text mb-2">{selectedItem.description}</p>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RARITY_COLORS[selectedItem.rarity] }} />
              <span className="text-sm capitalize" style={{ color: RARITY_COLORS[selectedItem.rarity] }}>{selectedItem.rarity}</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xl font-black text-cf-amber">{formatNumber(selectedItem.price)} TR</span>
                {selectedItem.originalPrice && (
                  <span className="text-sm text-cf-muted line-through ml-2">{formatNumber(selectedItem.originalPrice)}</span>
                )}
              </div>
              <button
                onClick={() => handlePurchase(selectedItem)}
                disabled={wallet.balance < selectedItem.price}
                className="cf-btn-primary"
              >
                {wallet.balance < selectedItem.price ? 'Not enough TR' : 'Purchase'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
